// ======================================================================
// FILE: src/modules/wms/template/components/TemplateChecklistsTab.js
// MODULE: Work Management System (WMS)
// FEATURE: Template Checklist Mapping
//
// PURPOSE:
//   Manage Template Level Checklist Assignments
//
// ARCHITECTURE:
//   Template
//      ↓
//   Checklists
//
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import wmsChecklistService
from "../../services/wmsChecklistService";

import wmsTemplateChecklistService
from "../../services/wmsTemplateChecklistService";

const DEFAULT_FORM = {

  checklist_id: "",

  sequence_no: 1,

  mandatory: true,

  remarks: "",
};

export default function TemplateChecklistsTab({
  templateId,
}) {

  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [checklists, setChecklists] =
    useState([]);

  const [showModal, setShowModal] =
    useState(false);

  const [editingRow, setEditingRow] =
    useState(null);

  const [form, setForm] =
    useState(DEFAULT_FORM);

  // ==========================================================
  // LOAD
  // ==========================================================

  useEffect(() => {

    if (templateId) {

      loadMappings();

      loadChecklists();
    }

  }, [templateId]);

  async function loadMappings() {

    try {

      setLoading(true);

      const data =
        await wmsTemplateChecklistService
          .getTemplateChecklists(
            templateId
          );

      setRows(data || []);

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Failed to load mappings"
      );

    } finally {

      setLoading(false);
    }
  }

  async function loadChecklists() {

    try {

      const result =
        await wmsChecklistService
          .listChecklists();

      setChecklists(
        result.items || []
      );

    } catch (err) {

      console.error(err);
    }
  }

  // ==========================================================
  // ADD
  // ==========================================================

  function handleAdd() {

    setEditingRow(null);

    setForm(
      DEFAULT_FORM
    );

    setShowModal(true);
  }

  // ==========================================================
  // EDIT
  // ==========================================================

  function handleEdit(row) {

    setEditingRow(row);

    setForm({

      checklist_id:
        row.checklist_id,

      sequence_no:
        row.sequence_no || 1,

      mandatory:
        row.mandatory ?? true,

      remarks:
        row.remarks || "",
    });

    setShowModal(true);
  }

  // ==========================================================
  // SAVE
  // ==========================================================

  async function handleSave() {

    if (!form.checklist_id) {

      alert(
        "Checklist is required"
      );

      return;
    }

    try {

      if (editingRow) {

        await
        wmsTemplateChecklistService
          .updateTemplateChecklist(
            templateId,
            editingRow.id,
            form
          );

      } else {

        await
        wmsTemplateChecklistService
          .createTemplateChecklist(
            templateId,
            form
          );
      }

      setShowModal(false);

      await loadMappings();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Save failed"
      );
    }
  }

  // ==========================================================
  // DELETE
  // ==========================================================

  async function handleDelete(row) {

    const ok =
      window.confirm(
        `Delete ${row.checklist_name}?`
      );

    if (!ok) {
      return;
    }

    try {

      await
      wmsTemplateChecklistService
        .deleteTemplateChecklist(
          templateId,
          row.id
        );

      await loadMappings();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Delete failed"
      );
    }
  }

  // ==========================================================
  // UI
  // ==========================================================

  return (

    <div>

      <div
        className="
          d-flex
          justify-content-between
          align-items-center
          mb-3
        "
      >

        <h5 className="mb-0">
          Assigned Checklists
        </h5>

        <button
          className="btn btn-primary"
          onClick={handleAdd}
        >
          <i className="fas fa-plus me-1" />
          Add Checklist
        </button>

      </div>

      <div className="table-responsive">

        <table
          className="
            table
            table-bordered
            table-striped
          "
        >

          <thead>

            <tr>

              <th>
                Sequence
              </th>

              <th>
                Checklist Code
              </th>

              <th>
                Checklist Name
              </th>

              <th>
                Mandatory
              </th>

              <th width="180">
                Actions
              </th>

            </tr>

          </thead>

          <tbody>

            {loading && (

              <tr>

                <td
                  colSpan="5"
                  className="text-center"
                >
                  Loading...
                </td>

              </tr>

            )}

            {!loading &&
             rows.length === 0 && (

              <tr>

                <td
                  colSpan="5"
                  className="text-center"
                >
                  No Checklists Assigned
                </td>

              </tr>

            )}

            {!loading &&
             rows.map((row) => (

              <tr key={row.id}>

                <td>
                  {row.sequence_no}
                </td>

                <td>
                  {row.checklist_code}
                </td>

                <td>
                  {row.checklist_name}
                </td>

                <td>

                  {row.mandatory
                    ? "Yes"
                    : "No"}

                </td>

                <td>

                  <button
                    className="
                      btn
                      btn-sm
                      btn-primary
                      me-1
                    "
                    onClick={() =>
                      handleEdit(row)
                    }
                  >
                    Edit
                  </button>

                  <button
                    className="
                      btn
                      btn-sm
                      btn-danger
                    "
                    onClick={() =>
                      handleDelete(row)
                    }
                  >
                    Delete
                  </button>

                </td>

              </tr>

            ))}

          </tbody>

        </table>

      </div>

      {/* ==================================================== */}
      {/* MODAL */}
      {/* ==================================================== */}

      {showModal && (

        <div
          className="modal d-block"
          tabIndex="-1"
        >

          <div className="modal-dialog">

            <div className="modal-content">

              <div className="modal-header">

                <h5>

                  {editingRow
                    ? "Edit Checklist"
                    : "Add Checklist"}

                </h5>

              </div>

              <div className="modal-body">

                <div className="mb-3">

                  <label>
                    Checklist *
                  </label>

                  <select
                    className="form-control"
                    value={
                      form.checklist_id
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        checklist_id:
                          e.target.value,
                      })
                    }
                  >

                    <option value="">
                      Select Checklist
                    </option>

                    {checklists.map(
                      (row) => (

                      <option
                        key={row.id}
                        value={row.id}
                      >
                        {row.checklist_code}
                        {" - "}
                        {row.checklist_name}
                      </option>

                    ))}
                  </select>

                </div>

                <div className="mb-3">

                  <label>
                    Sequence No
                  </label>

                  <input
                    type="number"
                    className="form-control"
                    value={
                      form.sequence_no
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        sequence_no:
                          e.target.value,
                      })
                    }
                  />

                </div>

                <div className="mb-3">

                  <div className="form-check">

                    <input
                      type="checkbox"
                      className="
                        form-check-input
                      "
                      checked={
                        form.mandatory
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          mandatory:
                            e.target.checked,
                        })
                      }
                    />

                    <label
                      className="
                        form-check-label
                      "
                    >
                      Mandatory
                    </label>

                  </div>

                </div>

                <div className="mb-3">

                  <label>
                    Remarks
                  </label>

                  <textarea
                    rows="3"
                    className="form-control"
                    value={
                      form.remarks
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        remarks:
                          e.target.value,
                      })
                    }
                  />

                </div>

              </div>

              <div className="modal-footer">

                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  Save
                </button>

                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    setShowModal(false)
                  }
                >
                  Cancel
                </button>

              </div>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}